import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AvatarIcon, CountryFlag, COUNTRY_NAME } from "@/lib/identity";
import ShareCardModal from "@/components/ShareCardModal";
import { PremiumBadge } from "@/components/PremiumBadge";
import { Crown, Trophy, ShareNetwork, ArrowLeft } from "@phosphor-icons/react";

/**
 * PublicProfile — `/u/:handle` — no login required. `handle` is either a
 * username or a user_id (backend fallback handles both).
 *
 * Serves as the permanent shareable "NK Card" surface. Includes an inline
 * "Share your card" button that opens the ShareCardModal.
 */
export default function PublicProfile() {
  const { handle } = useParams();
  const [profile, setProfile] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [season, setSeason] = useState(null);

  useEffect(() => {
    api.get(`/season/profile/${handle}`).then((r) => setProfile(r.data)).catch(() => setProfile({ error: "not_found" }));
    api.get("/season/current").then((r) => setSeason(r.data)).catch(() => {});
  }, [handle]);

  if (!profile) return <div className="p-8 text-nk-mute" data-testid="public-profile-loading">Loading…</div>;
  if (profile.error) {
    return (
      <div className="max-w-lg mx-auto p-8 text-center" data-testid="public-profile-not-found">
        <p className="eyebrow mb-2">Not found</p>
        <h1 className="font-display text-2xl mb-3">No player at that handle</h1>
        <p className="text-sm text-nk-mute mb-6">
          Check the URL, or head back to the leaderboard to find players.
        </p>
        <Link to="/leaderboard" className="text-nk-gold text-sm inline-flex items-center gap-1.5">
          <ArrowLeft size={13} weight="bold" /> Back to leaderboard
        </Link>
      </div>
    );
  }

  const u = profile.user || {};
  const s = profile.stats || {};
  const rank = profile.season_rank;
  const titles = profile.champion_titles || [];

  return (
    <>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="public-profile">
        <Link to="/leaderboard" className="text-nk-mute text-xs inline-flex items-center gap-1.5 hover:text-nk-text">
          <ArrowLeft size={12} weight="bold" /> Leaderboard
        </Link>

        {/* Hero NK Card */}
        <div className="nk-card p-6 sm:p-8 border-l-2 border-nk-gold relative overflow-hidden">
          <div className="flex items-start gap-4 flex-wrap">
            <AvatarIcon id={u.avatar} size={72} />
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-white">
                  {u.username || "Player"}
                </h1>
                <PremiumBadge active={u.nk_plus_active} />
              </div>
              <div className="flex items-center gap-2 mt-2 text-sm text-nk-mute">
                <CountryFlag country={u.country} size={14} />
                <span>Playing for {COUNTRY_NAME[u.country] || u.country || "the world"}</span>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-5 max-w-md">
                <HeroStat label="Season score" value={(s.season_score || 0).toLocaleString()} accent />
                <HeroStat label="Rank" value={rank ? `#${rank}` : "—"} />
                <HeroStat label="Rating" value={s.knowledge_rating || 1000} />
              </div>
            </div>
            <button
              onClick={() => setShareOpen(true)}
              data-testid="public-profile-share-btn"
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 border border-nk-gold/60 text-nk-gold hover:bg-nk-gold/10 rounded-sm text-xs"
            >
              <ShareNetwork size={13} weight="bold" /> Share NK Card
            </button>
          </div>
        </div>

        {/* Championship titles */}
        {titles.length > 0 && (
          <div className="nk-card p-5" data-testid="public-profile-titles">
            <p className="eyebrow text-nk-gold mb-3">Hall of Fame</p>
            <ul className="space-y-2">
              {titles.map((t, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  {t.level === "international"
                    ? <Trophy size={14} weight="fill" className="text-nk-gold" />
                    : <Crown size={14} weight="fill" className="text-nk-text" />}
                  <span className="font-medium">
                    {t.level === "international" ? "Continental Champion" : "National Champion"}
                  </span>
                  <span className="text-nk-mute">· Season {t.season_number}</span>
                  {t.country && <CountryFlag country={t.country} size={11} />}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Stat grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Ladder record" value={s.ladder_record || 0} />
          <MiniStat label="Full clears" value={s.ladder_full_clears || 0} />
          <MiniStat label="Daily wins" value={s.daily_wins || 0} />
          <MiniStat label="Duel wins" value={s.duel_wins || 0} />
        </div>

        <div className="text-center pt-4 border-t border-nk-line">
          <p className="text-xs text-nk-mute mb-2">Want to challenge {u.username}?</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-nk-accent text-nk-bg text-sm font-medium rounded-sm"
            data-testid="public-profile-join-cta"
          >
            Join NKognito — free
          </Link>
        </div>
      </div>

      <ShareCardModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        profile={profile}
        seasonNumber={season?.season_number}
      />
    </>
  );
}

function HeroStat({ label, value, accent = false }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-[0.18em] text-nk-mute font-mono mb-1">{label}</p>
      <p className={`font-mono text-2xl font-medium ${accent ? "text-nk-gold" : "text-white"}`}>{value}</p>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="nk-card p-3 text-center">
      <p className="text-[9px] uppercase tracking-widest text-nk-mute font-mono mb-1">{label}</p>
      <p className="font-mono text-lg font-medium text-nk-text">{value}</p>
    </div>
  );
}
